import React, { useEffect, useState } from "react";
import Dialog from "Components/DesignSystem/Dialog/Dialog";
import Button from "Components/DesignSystem/Button/Button";
import Alert from "Components/DesignSystem/Alert/Alert";
import Checkbox from "Components/DesignSystem/Checkbox/Checkbox";
import Typography from "Components/DesignSystem/Typography/Typography";
import { DELETE_ALL_ENVIRONMENTS_LABEL, DELETE_ENVIRONMENT_WARNING } from "../ProductConstants";

interface ProductDeleteConfirmModalProps {
  showModal: boolean;
  closeModal: () => void;
  totalProducts: number;
  /** Receives the "delete from all other environments" choice for the whole selection. */
  onConfirm: (allEnvironments: boolean) => void;
  loading: boolean;
  /** `gmsproducts_fulldelete`. Without it the checkbox is not rendered and Delete sends `false`. */
  canDeleteAllEnvironments?: boolean;
}

/**
 * The "are you sure" step of the product list's Bulk Action › Delete.
 *
 * On DS `Dialog` (was the legacy `ModalContainer` + `FillButton`/`BorderedButton` with a
 * hand-rolled alert box). The warning is a DS `Alert`, which carries the dialog's one
 * danger mark — the same § 166 rule `PDW/ConfirmBulkDelete` follows, so no `tone` here.
 *
 * The checkbox is the batch's single decision: unchecked removes the products from the
 * current environment only; checked removes them from every environment of this domain
 * in the current workspace. It resets to unchecked every time the dialog opens, so a
 * previous choice never silently carries into the next delete. It is rendered only for
 * `gmsproducts_fulldelete` holders (`canDeleteAllEnvironments`, hidden by default).
 *
 * `Dialog` has no subtitle slot, so the "X Selected" count leads the body.
 * TODO: move it into the header if `Dialog` gains a subtitle.
 */
const ProductDeleteConfirmationModal = ({
  showModal,
  closeModal,
  totalProducts,
  onConfirm,
  loading,
  canDeleteAllEnvironments = false,
}: ProductDeleteConfirmModalProps) => {
  const [allEnvironments, setAllEnvironments] = useState<boolean>(false);

  useEffect(() => {
    if (showModal) setAllEnvironments(false);
  }, [showModal]);

  const footer = (
    <div className="tw-flex tw-w-full tw-items-center tw-justify-end tw-gap-3">
      <Button
        variant="secondary"
        id="product-delete-confirm-modal-cancel-btn"
        data-testid="product-delete-confirm-modal-cancel-btn"
        disabled={loading}
        onClick={() => closeModal()}
      >
        Cancel
      </Button>
      <Button
        variant="danger-primary"
        id="product-delete-confirm-modal-confirm-btn"
        data-testid="product-delete-confirm-modal-confirm-btn"
        loading={loading}
        onClick={() => onConfirm(canDeleteAllEnvironments && allEnvironments)}
      >
        Delete
      </Button>
    </div>
  );

  return (
    <Dialog
      open={showModal}
      onClose={() => { if (!loading) closeModal(); }}
      title="Bulk Delete"
      footer={footer}
      data-testid="product-delete-confirm-modal"
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Typography
          variant="caption-metadata"
          as="p"
          className="tw-text-ink-secondary"
          data-testid="product-delete-confirm-modal-count"
        >
          {totalProducts} Selected
        </Typography>
        <Alert tone="danger" data-testid="product-delete-confirm-modal-warning">
          {DELETE_ENVIRONMENT_WARNING}
        </Alert>
        {canDeleteAllEnvironments && (
          <Checkbox
            label={DELETE_ALL_ENVIRONMENTS_LABEL}
            checked={allEnvironments}
            onChange={(checked) => setAllEnvironments(checked)}
            disabled={loading}
            data-testid="product-delete-confirm-modal-all-environments"
          />
        )}
        <Typography variant="body-primary" as="p">
          Would you like to proceed?
        </Typography>
      </div>
    </Dialog>
  );
};

export default ProductDeleteConfirmationModal;
